import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Auth.css';

import { API_URL } from '../../config/api';
import { showError, showSuccess } from '../../utils/toast';
import { getPostLoginPath } from '../../super-admin/auth';
import { channelLabel, formatPhone } from './PhoneField';
const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

const errorText = (data, fallback) => {
    const msg = data?.message ?? data?.error;
    if (Array.isArray(msg)) return msg.filter(Boolean).join(' ') || fallback;
    return typeof msg === 'string' && msg ? msg : fallback;
};

function VerifyOTP() {
    const location = useLocation();
    const byPhone = location.state?.verifyBy === 'phone';
    const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(
        Number(location.state?.resendInSeconds) || RESEND_COOLDOWN,
    );
    const [phone, setPhone] = useState(location.state?.phone || '');
    const [channel, setChannel] = useState(location.state?.channel || 'sms');
    const inputRefs = useRef([]);
    const navigate = useNavigate();

    const emailFromState = location.state?.email;
    const emailFromQuery = new URLSearchParams(location.search).get('email');
    const email = (emailFromState || emailFromQuery || '').trim();

    // Redirect to register if no email in state or query string
    useEffect(() => {
        if (!email) {
            navigate('/register', { replace: true });
        }
    }, [email, navigate]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return;

        const timer = setInterval(() => {
            setResendCooldown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [resendCooldown]);

    // Auto-focus first input on mount
    useEffect(() => {
        if (email && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [email]);

    const handleVerify = useCallback(async (otpCode) => {
        const code = otpCode || otp.join('');
        if (code.length !== OTP_LENGTH) {
            showError('Please enter the complete 6-digit code');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                `${API_URL}${byPhone ? '/auth/register/verify-phone' : '/auth/verify-otp'}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(byPhone ? { email, code } : { email, otp: code }),
                },
            );

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                navigate(getPostLoginPath(data.user));
            } else {
                showError(errorText(data, 'Verification failed. Please try again.'));
                setOtp(new Array(OTP_LENGTH).fill(''));
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            }
        } catch (err) {
            showError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [byPhone, email, navigate, otp]);

    const handleChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-advance to next input
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1].focus();
        }

        // Auto-submit when all digits are entered
        if (value && index === OTP_LENGTH - 1) {
            const code = newOtp.join('');
            if (code.length === OTP_LENGTH) {
                handleVerify(code);
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Move to previous input on backspace if current is empty
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        // Only process if it looks like a 6-digit code
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);

            // Focus last input
            inputRefs.current[OTP_LENGTH - 1].focus();

            // Auto-submit
            handleVerify(pastedData);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;

        try {
            const response = await fetch(
                `${API_URL}${byPhone ? '/auth/register/resend-phone-otp' : '/auth/resend-otp'}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                },
            );

            const data = await response.json();

            if (response.ok) {
                if (data.phone) setPhone(data.phone);
                if (data.channel) setChannel(data.channel);
                showSuccess(
                    byPhone
                        ? 'A new verification code has been sent to your mobile number.'
                        : 'A new verification code has been sent to your email.',
                );
                setResendCooldown(Number(data.resendInSeconds) || RESEND_COOLDOWN);
                setOtp(new Array(OTP_LENGTH).fill(''));
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            } else {
                showError(errorText(data, 'Failed to resend code. Please try again.'));
            }
        } catch (err) {
            showError('Network error. Please try again.');
        }
    };

    if (!email) return null;

    return (
        <div className="auth-container">
            <div className="auth-form-section">
                <div className="auth-form-container">
                    <a className="auth-logo" href="https://tracktcrm.com/" target="_blank" rel="noopener noreferrer" aria-label="TracktCRM">
                        <img
                            src={`${process.env.PUBLIC_URL || ''}/brand/tracktcrm-auth-logo.png`}
                            alt="TracktCRM"
                        />
                    </a>
                    {byPhone ? (
                        <div className="auth-header">
                            <h1>Verify Your Mobile Number</h1>
                            <p>We've sent a 6-digit verification code {channelLabel(channel)} to</p>
                            <p className="otp-email-highlight">{phone ? formatPhone(phone) : 'your mobile number'}</p>
                        </div>
                    ) : (
                        <div className="auth-header">
                            <h1>Verify Your Email</h1>
                            <p>We've sent a 6-digit verification code to</p>
                            <p className="otp-email-highlight">{email}</p>
                        </div>
                    )}

                    <div className="otp-form">
                        <div className="otp-inputs">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(ref) => (inputRefs.current[index] = ref)}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    className="otp-input"
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                    disabled={loading}
                                />
                            ))}
                        </div>

                        <button
                            type="button"
                            className="auth-button"
                            onClick={() => handleVerify()}
                            disabled={loading || otp.join('').length !== OTP_LENGTH}
                        >
                            {loading ? 'Verifying...' : byPhone ? 'Verify and continue' : 'Verify Email'}
                        </button>

                        <div className="otp-info">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            <span>
                                {byPhone
                                    ? 'If the code has expired, ask for a new one below'
                                    : 'The verification code expires after 10 minutes'}
                            </span>
                        </div>

                        <div className="resend-section">
                            <span>Didn't receive the code?</span>
                            {resendCooldown > 0 ? (
                                <span className="resend-timer">Resend code in {resendCooldown}s</span>
                            ) : (
                                <button
                                    type="button"
                                    className="resend-button"
                                    onClick={handleResend}
                                >
                                    Resend Code
                                </button>
                            )}
                        </div>
                    </div>

                    <Link to="/register" className="auth-back-link">
                        ← Back to Register
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default VerifyOTP;
